import React, { useEffect, useRef, useState } from "react";
import DesktopTransactionApprovalItems from "./DesktopTransactionApprovalItems";
import BulkEditModal from "./BulkEditModal";
import Api from "../../utils/Api";
import { setBadge } from "../../utils/push";

export default function DesktopTransactionApproval({ inquiry }) {
    const [lookups, setLookups] = useState(null);
    const [items, setItems] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [selectedCount, setSelectedCount] = useState(0);
    const transactionListRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            const { response: fetchedLookups } = await Api.lookups();
            setLookups(fetchedLookups);

            const transactionItems = await getUnapprovedTransactions(inquiry);
            if (transactionListRef.current) {
                transactionListRef.current.setLookups(fetchedLookups);
                transactionListRef.current.setItems(transactionItems);
            }

            setItems(transactionItems);
        };

        fetchData();
    }, [inquiry]);

    useEffect(() => {
        // The inquiry list is a different subset, so it must not drive the badge.
        if (!inquiry) {
            setBadge(items.length);
        }
    }, [items, inquiry]);

    const getUnapprovedTransactions = async (inquiryOnly) => {
        if (inquiryOnly) {
            const {
                response: { items }
            } = await Api.getTransactionInquiry();
            return items;
        } else {
            const {
                response: { items }
            } = await Api.getUnapprovedTransactions();
            return items;
        }
    };

    const sortByPredictionGradeAndCategory = () => {
        transactionListRef.current?.sortByPredictionGradeAndCategory();
    };

    const sortByTime = () => {
        transactionListRef.current?.sortByDate();
    };

    const filterByText = (e) => {
        transactionListRef.current?.filterByText(e.target.value);
    };

    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode);
        // Clear selections when exiting selection mode
        if (selectionMode) {
            transactionListRef.current?.clearSelection();
            setSelectedCount(0);
        }
    };

    const updateSelectedCount = (count) => {
        setSelectedCount(count);
    };

    const openBulkEditModal = () => {
        setShowBulkEditModal(true);
    };

    const closeBulkEditModal = () => {
        setShowBulkEditModal(false);
    };

    const handleBulkSave = async (bulkEditData) => {
        // Get selected transactions
        const selectedTransactions = transactionListRef.current?.getSelectedTransactions() || [];
        
        if (selectedTransactions.length === 0) {
            return;
        }

        // Prepare bulk update items - only update fields that were selected in the modal
        const bulkUpdateItems = selectedTransactions.map(transaction => {
            const updateItem = {
                id: transaction.id,
                type: transaction.type,
                remarks: transaction.remarks
            };

            // Only include categoryId if it was selected in the modal
            if (bulkEditData.categoryId) {
                updateItem.categoryId = bulkEditData.categoryId;
            } else {
                updateItem.categoryId = transaction.categoryId;
            }

            // Only include tags if they were selected in the modal
            if (bulkEditData.tags && bulkEditData.tags.length > 0) {
                updateItem.tags = bulkEditData.tags;
            } else {
                updateItem.tags = transaction.tags;
            }

            return updateItem;
        });

        try {
            // Call bulk approval API
            const { response } = await Api.bulkApproveTransactions(bulkUpdateItems);
            
            if (response.success) {
                // Remove all successfully approved transactions from the list
                const approvedIds = response.approvedIds || bulkUpdateItems.map(item => item.id);
                approvedIds.forEach(id => {
                    transactionListRef.current?.removeTransaction(id);
                });
                
                // Show success notification
                transactionListRef.current?.showBulkSaveSuccess(approvedIds.length);
                
                // Show error notification if some failed
                if (response.failedCount > 0) {
                    transactionListRef.current?.showBulkSaveError(response.failedCount);
                }
            } else {
                // Show error notification
                transactionListRef.current?.showBulkSaveError(bulkUpdateItems.length);
            }
        } catch (error) {
            console.error("Error in bulk approval:", error);
            transactionListRef.current?.showBulkSaveError(bulkUpdateItems.length);
        }

        // Close modal and clear selections
        setShowBulkEditModal(false);
        transactionListRef.current?.clearSelection();
        setSelectedCount(0);
    };

    return (
        <div>
            <div
                style={{
                    display: "flex",
                    direction: "rtl",
                    margin: "auto",
                    marginTop: "80px",
                    marginBottom: "20px",
                    width: "50%",
                }}
            >
                <button onClick={sortByTime} type="button" className="btn btn-info">
                    מיין לפי תאריך
                </button>

                <button
                    style={{ marginRight: "20px" }}
                    onClick={sortByPredictionGradeAndCategory}
                    type="button"
                    className="btn btn-info"
                >
                    מיין לפי ציון וקטגוריה
                </button>

                <button
                    style={{
                        marginRight: "20px",
                        backgroundColor: selectionMode ? "#28a745" : "#FF6B6B",
                        borderColor: selectionMode ? "#28a745" : "#FF6B6B",
                        color: "white"
                    }}
                    onClick={toggleSelectionMode}
                    type="button"
                    className="btn"
                >
                    {selectionMode ? 'ביטול בחירה' : 'בחירה מרובה'}
                </button>

                {selectionMode && selectedCount > 0 && (
                    <button
                        style={{ marginRight: "20px" }}
                        onClick={openBulkEditModal}
                        type="button"
                        className="btn btn-primary"
                    >
                        עדכון מרובה ({selectedCount})
                    </button>
                )}

                <h4 style={{ marginRight: "20px" }}>סנן</h4>
                <input style={{ marginRight: "10px" }} type="text" onChange={filterByText} />
            </div>

            <DesktopTransactionApprovalItems
                approvalMode={true}
                ref={transactionListRef}
                lookups={lookups}
                selectionMode={selectionMode}
                onSelectionCountChange={updateSelectedCount}
            />

            <BulkEditModal
                show={showBulkEditModal}
                onHide={closeBulkEditModal}
                lookups={lookups}
                onSave={handleBulkSave}
            />
        </div>
    );
}
